import {
  buildXhrDiagnosticObject,
  isXhrError,
} from 'infrastructure/assets/loggingUtils';
import { getConnectionInfo } from 'infrastructure/diagnostics/collectEnvironment';

const MiB = 1024 * 1024;
const R2_PART_SIZE = 8 * MiB; // Optimal for R2 performance
const R2_PART_SIZE_SLOW_CONNECTION = 4 * MiB;
const R2_MULTIPART_THRESHOLD = 15 * MiB; // Files larger than this use multipart
const R2_MULTIPART_THRESHOLD_SLOW_CONNECTION = R2_PART_SIZE_SLOW_CONNECTION; // Files larger than this use multipart
const R2_TIMEOUT = 30_000; // 30 seconds for good connections
const R2_TIMEOUT_SLOW_CONNECTION = 120_000; // 2 minutes for slow connections

// Retry configuration
export interface RetryConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  retryableStatuses?: number[];
  onRetry?: (attempt: number, error: any) => void;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
  retryableStatuses: [408, 429, 500, 502, 503, 504], // Timeout, rate limit, server errors
};

function isSlowConnection(): boolean {
  const connection = getConnectionInfo();

  return (
    connection.effectiveType === 'slow-2g' ||
    connection.effectiveType === '2g' ||
    connection.effectiveType === '3g' ||
    connection.saveData ||
    // High RTT indicates poor connection
    (connection.rtt ? connection.rtt > 500 : false)
  );
}

// Determine if we should use reduced concurrency based on connection
export function getAdaptiveConcurrency(defaultConcurrency: number): number {
  const conn = getConnectionInfo();

  if (!conn.isOnline) return 1;

  // Reduce concurrency on slow connections
  if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') {
    return 1;
  }

  if (conn.effectiveType === '3g' || conn.saveData) {
    return Math.min(2, defaultConcurrency);
  }

  // High RTT indicates poor connection
  if (conn.rtt && conn.rtt > 500) {
    return Math.min(2, defaultConcurrency);
  }

  return defaultConcurrency;
}

export function getAdaptivePartSize(): number {
  if (isSlowConnection()) {
    return R2_PART_SIZE_SLOW_CONNECTION;
  }

  return R2_PART_SIZE;
}

export function getAdaptiveMultipartThreshold(): number {
  if (isSlowConnection()) {
    return R2_MULTIPART_THRESHOLD_SLOW_CONNECTION;
  }

  return R2_MULTIPART_THRESHOLD;
}

export function getAdaptiveTimeout(): number {
  if (isSlowConnection()) {
    return R2_TIMEOUT_SLOW_CONNECTION;
  }

  return R2_TIMEOUT;
}
// Calculate delay with exponential backoff and jitter
export function calculateRetryDelay(
  attempt: number,
  config: RetryConfig
): number {
  const exponentialDelay =
    config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt - 1);
  const clampedDelay = Math.min(exponentialDelay, config.maxDelayMs);
  // Add jitter (±25%) to prevent thundering herd
  const jitter = clampedDelay * 0.25 * (Math.random() * 2 - 1);
  return Math.round(clampedDelay + jitter);
}

// Wait for online status with timeout
export function waitForOnline(timeoutMs: number = 30000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (navigator.onLine) {
      resolve();
      return;
    }

    const timeout = setTimeout(() => {
      window.removeEventListener('online', handleOnline);
      reject(new Error('Timeout waiting for online status'));
    }, timeoutMs);

    const handleOnline = () => {
      clearTimeout(timeout);
      window.removeEventListener('online', handleOnline);
      resolve();
    };

    window.addEventListener('online', handleOnline);
  });
}

// Determine if an error is retryable
export function isRetryableError(error: any, config: RetryConfig): boolean {
  if (isXhrError(error)) {
    // Network errors are generally retryable
    if (
      error.diagnostics.phase === 'error' ||
      error.diagnostics.phase === 'timeout'
    ) {
      return true;
    }

    // Abort is not retryable
    if (error.diagnostics.phase === 'abort') {
      return false;
    }
  }

  // Check status codes
  if (error.status && config.retryableStatuses) {
    return config.retryableStatuses.includes(error.status);
  }

  // Connection lost during upload
  if (!navigator.onLine) {
    return true;
  }

  return false;
}

// Enhanced XHR wrapper with retry logic
export async function xhrWithRetry(
  method: 'PUT',
  url: string,
  correlationId: string,
  data: Buffer | Blob | ArrayBuffer,
  headers: Record<string, string>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  onProgress?: (loaded: number, total: number) => void,
  signal?: AbortSignal
): Promise<XMLHttpRequest> {
  let lastError: any;

  for (let attempt = 1; attempt <= config.maxRetries; attempt++) {
    try {
      // Check online status before attempting
      if (!navigator.onLine) {
        await waitForOnline(5000);
      }

      const xhr = await performXhrRequest(
        method,
        correlationId,
        url,
        data,
        headers,
        onProgress,
        signal
      );

      return xhr;
    } catch (error: any) {
      const errorWithAttemptNumber = isXhrError(error)
        ? {
            ...error,
            diagnostics: { ...error.diagnostics, attempt },
          }
        : error;

      lastError = errorWithAttemptNumber;

      // Check if we should retry
      if (attempt < config.maxRetries && isRetryableError(error, config)) {
        if (config.onRetry) {
          config.onRetry(attempt, error);
        }

        // Wait before retrying
        const delay = calculateRetryDelay(attempt, config);
        await new Promise(resolve => setTimeout(resolve, delay));

        // Wait for online if offline
        if (!navigator.onLine) {
          try {
            await waitForOnline(30000);
          } catch {
            // Continue with retry anyway
          }
        }
      } else {
        // Not retryable or max retries reached
        throw errorWithAttemptNumber;
      }
    }
  }

  throw lastError;
}

// Perform single XHR request
function performXhrRequest(
  method: 'PUT',
  correlationId: string,
  url: string,
  data: Buffer | Blob | ArrayBuffer,
  headers: Record<string, string>,
  onProgress?: (loaded: number, total: number) => void,
  signal?: AbortSignal
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    let hadUploadProgress = false;
    let uploadedBytes = 0;

    const error = {
      correlationId,
      method,
      xhr,
      url,
    };

    // Configure timeout based on connection speed
    xhr.timeout = getAdaptiveTimeout();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
          uploadedBytes = event.loaded;
          if (event.loaded > 0) {
            hadUploadProgress = true;
          }
        }
      });
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
      } else {
        reject(
          buildXhrDiagnosticObject({
            ...error,
            hadUploadProgress,
            uploadedBytes,
            phase: 'error',
          })
        );
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(
        buildXhrDiagnosticObject({
          ...error,
          hadUploadProgress,
          uploadedBytes,
          phase: 'error',
        })
      );
    });

    // Handle timeout
    xhr.addEventListener('timeout', () => {
      reject(
        buildXhrDiagnosticObject({
          ...error,
          hadUploadProgress,
          uploadedBytes,
          phase: 'timeout',
        })
      );
    });

    // Handle abort
    xhr.addEventListener('abort', () => {
      reject(
        buildXhrDiagnosticObject({
          ...error,
          hadUploadProgress,
          uploadedBytes,
          phase: 'abort',
        })
      );
    });

    // Handle abort signal
    if (signal) {
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    // Start the request
    xhr.open(method, url);

    // Set headers
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    xhr.send(data);
  });
}
